import React, { useState, useEffect, useRef } from "react";
import {
  useCreateLearningResourceMutation,
  useUpdateLearningResourceMutation,
} from "../../../services/api";
import ErrorMessage from "../../../Components/common/ErrorMessage";
import "./LearningResourceForm.css";

const IconClose = (props) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconUpload = (props) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const RESOURCE_TYPES = [
  "Articles",
  "Videos",
  "Exercises",
  "Courses",
  "Workshops",
  "Other",
];

export default function LearningResourceForm({ resource, onClose }) {
  const isEditMode = !!resource;
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    type: "Articles",
    sourceType: "url",
    url: "",
    isActive: true,
  });

  const [selectedFile, setSelectedFile] = useState(null);
  const [validationError, setValidationError] = useState("");

  const [createResource, { isLoading: isCreating, error: createError }] =
    useCreateLearningResourceMutation();
  const [updateResource, { isLoading: isUpdating, error: updateError }] =
    useUpdateLearningResourceMutation();

  // Load resource data in edit mode
  useEffect(() => {
    if (resource) {
      setFormData({
        title: resource.title || "",
        description: resource.description || "",
        type: resource.type || "Articles",
        sourceType: resource.sourceType || "url",
        url: resource.url || "",
        isActive: resource.isActive !== undefined ? resource.isActive : true,
      });
    }
  }, [resource]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
    if (validationError) setValidationError("");
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Check file size (100MB limit)
      if (file.size > 100 * 1024 * 1024) {
        setValidationError("File size must be less than 100MB");
        return;
      }
      setSelectedFile(file);
      setFormData({ ...formData, sourceType: "file" });
    }
    if (validationError) setValidationError("");
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidationError("");

    // Validation
    if (!formData.title.trim()) {
      setValidationError("Title is required");
      return;
    }

    if (formData.sourceType === "url" && !formData.url.trim()) {
      setValidationError("URL is required when source type is URL");
      return;
    }

    if (formData.sourceType === "file" && !selectedFile && !isEditMode) {
      setValidationError("Please select a file to upload");
      return;
    }

    try {
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("description", formData.description);
      submitData.append("type", formData.type);
      submitData.append("sourceType", formData.sourceType);
      submitData.append("isActive", formData.isActive);

      if (formData.sourceType === "url") {
        submitData.append("url", formData.url);
      } else if (selectedFile) {
        submitData.append("file", selectedFile);
      }

      if (isEditMode) {
        await updateResource({ id: resource._id, formData: submitData }).unwrap();
      } else {
        await createResource(submitData).unwrap();
      }

      onClose();
    } catch (err) {
      console.error("Failed to save resource:", err);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isLoading = isCreating || isUpdating;
  const error = createError || updateError;

  return (
    <div className="resource-form-overlay" onClick={handleOverlayClick}>
      <div className="resource-form-modal">
        {/* Header */}
        <div className="resource-form-header">
          <h2>{isEditMode ? "Edit Resource" : "Add New Resource"}</h2>
          <button
            type="button"
            className="resource-form-close"
            onClick={onClose}
            aria-label="Close"
          >
            <IconClose />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="resource-form">
          {error && <ErrorMessage error={error} />}
          {validationError && (
            <div className="validation-error">{validationError}</div>
          )}

          <div className="form-group">
            <label htmlFor="title">
              Title <span className="required">*</span>
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter resource title"
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter resource description"
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="type">
                Type <span className="required">*</span>
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                required
              >
                {RESOURCE_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span>Active</span>
              </label>
              <small className="form-hint">
                Inactive resources won't appear in the library
              </small>
            </div>
          </div>

          {/* Source Type Selection */}
          <div className="form-group">
            <label>Source Type <span className="required">*</span></label>
            <div className="source-type-toggle">
              <button
                type="button"
                className={`source-toggle-btn ${formData.sourceType === "url" ? "active" : ""}`}
                onClick={() => setFormData({ ...formData, sourceType: "url" })}
              >
                External URL
              </button>
              <button
                type="button"
                className={`source-toggle-btn ${formData.sourceType === "file" ? "active" : ""}`}
                onClick={() => setFormData({ ...formData, sourceType: "file" })}
              >
                Upload File
              </button>
            </div>
          </div>

          {/* URL Input */}
          {formData.sourceType === "url" && (
            <div className="form-group">
              <label htmlFor="url">
                URL <span className="required">*</span>
              </label>
              <input
                type="url"
                id="url"
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder="https://example.com/resource"
                required={formData.sourceType === "url"}
              />
            </div>
          )}

          {/* File Upload */}
          {formData.sourceType === "file" && (
            <div className="form-group">
              <label>
                File {!isEditMode && <span className="required">*</span>}
              </label>
              <div className="file-upload-area">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.mp4,.webm,.mov,.mp3,.wav,.jpg,.jpeg,.png,.gif,.webp"
                  style={{ display: "none" }}
                />

                {selectedFile ? (
                  <div className="selected-file">
                    <span className="file-name">{selectedFile.name}</span>
                    <span className="file-size">
                      ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                    <button
                      type="button"
                      className="remove-file-btn"
                      onClick={handleRemoveFile}
                    >
                      Remove
                    </button>
                  </div>
                ) : isEditMode && resource?.file?.filename ? (
                  <div className="existing-file">
                    <span className="file-name">Current: {resource.file.filename}</span>
                    <button
                      type="button"
                      className="upload-new-btn"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload New File
                    </button>
                  </div>
                ) : (
                  <div
                    className="upload-dropzone"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <IconUpload />
                    <span>Click to upload a file</span>
                    <small>PDF, Word, Excel, PowerPoint, Images, Video, Audio (max 100MB)</small>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="resource-form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update Resource"
                : "Create Resource"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
